import React, { useState, useEffect } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { ArrowRight, CheckCircle, Loader, X, Plus, Truck, Lock, Sparkles, Mail, Phone, Clock } from 'lucide-react'
import PwaInstallBanner from './PwaInstallBanner'

// Paleta LogNow Store - Dark + Yellow editorial
const T = {
  black: '#000000',
  bgDark: '#020202',
  zinc950: '#09090b',
  zinc900: '#18181b',
  yellow: '#FACC15',
  yellow50: '#FEF9C3',
  yellow400Light: 'rgba(250,204,21,0.12)',
  yellow700: '#A16207',
  white: '#FFFFFF',
  white70: 'rgba(255,255,255,0.70)',
  white40: 'rgba(255,255,255,0.40)',
  white10: 'rgba(255,255,255,0.10)',
  white05: 'rgba(255,255,255,0.05)',
  slate900: '#0F172A',
  slate600: '#475569',
  slate100: '#F1F5F9',
  success: '#16A34A',
  danger: '#EF4444',
}

const LOGO_DARK = '/logo-dark.png'    // logo escuro -> usar sobre fundo claro/branco/amarelo
const LOGO_CLARO = '/logo-claro.png'  // logo claro -> usar sobre fundo escuro/preto

const MARCAS = [
  'CHANEL', 'DIOR', 'LA MER', 'TOM FORD', 'ESTÉE LAUDER', 'YSL',
  'ARMANI', 'LANCÔME', 'HERMÈS', 'CLINIQUE', 'K18', 'GUERLAIN',
  'SKINCEUTICALS', 'SOL DE JANEIRO', 'DIPTYQUE', 'VALENTINO',
  'SHISEIDO', 'CLARINS', 'ELIZABETH ARDEN', 'KIEHL\'S', 'SKIN1004',
]

const FAQ = [
  {
    q: 'Quem pode pedir acesso?',
    a: 'Profissionais do sector — perfumarias, barbearias, salões, lojas online, revendedores. Validamos NIF e perfil antes de aprovar.',
  },
  {
    q: 'Quanto demora a aprovação?',
    a: 'Em média, 24 horas úteis. Recebes resposta por email com link para definires password.',
  },
  {
    q: 'Há mínimos de encomenda?',
    a: 'Não há mínimos absurdos. Mínimo é por produto (10un por defeito, ajustável). Portes incluídos no preço — sem custos escondidos.',
  },
  {
    q: 'Como funciona o pagamento?',
    a: 'Depende do prazo de entrega do artigo: os de entrega rápida pagam-se a 100% à confirmação da encomenda; os de prazo mais longo em duas prestações — 50% à confirmação + 50% antes do envio. Sempre por transferência bancária, com recibo a cada pagamento. O carrinho diz-te qual se aplica antes de finalizares.',
  },
  {
    q: 'Em quantos dias recebo a encomenda?',
    a: 'Varia com o artigo — há artigos de entrega em poucos dias e outros de prazo mais longo. O prazo está indicado no catálogo e conta-se após confirmação do pagamento. Acompanhas o estado em tempo real na tua área de cliente.',
  },
  {
    q: 'Os preços ficam só comigo?',
    a: 'Sim. O catálogo é privado e os teus preços não são partilhados com outros clientes. Há produtos exclusivos para ti se houver associação.',
  },
]

export default function LandingPage({ onShowLogin }) {
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', nif: '', mensagem: '', hp: '' })
  const [enviando, setEnviando] = useState(false)
  const [msg, setMsg] = useState({ tipo: '', texto: '' })
  const [legal, setLegal] = useState({})
  const [legalModal, setLegalModal] = useState(null) // 'termos' | 'privacidade' | null

  // Permitir scroll da pagina (index.css tem overflow:hidden global p/ admin)
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = { o: html.style.overflow, h: html.style.height }
    const prevBody = { o: body.style.overflow, h: body.style.height }
    html.style.overflow = 'auto'
    html.style.height = 'auto'
    body.style.overflow = 'auto'
    body.style.height = 'auto'
    const root = document.getElementById('root')
    const prevRoot = root ? { o: root.style.overflow, h: root.style.height } : null
    if (root) { root.style.overflow = 'visible'; root.style.height = 'auto' }
    return () => {
      html.style.overflow = prevHtml.o; html.style.height = prevHtml.h
      body.style.overflow = prevBody.o; body.style.height = prevBody.h
      if (root && prevRoot) { root.style.overflow = prevRoot.o; root.style.height = prevRoot.h }
    }
  }, [])

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('config_sistema').select('chave, valor')
        .in('chave', ['legal_nome','legal_nif','legal_morada','legal_cod_post','legal_telefone','legal_marca','livro_reclamacoes_url'])
      const obj = {}
      for (const r of (data || [])) obj[r.chave] = r.valor || ''
      setLegal(obj)
    })()
  }, [])

  const submit = async (e) => {
    e.preventDefault()
    setEnviando(true); setMsg({ tipo: '', texto: '' })
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/pedir-acesso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'Erro ao enviar pedido')
      if (data.ja_cliente) {
        setMsg({ tipo: 'ok', texto: 'Já tens conta. Usa o botão "Entrar" para aceder.' })
      } else if (data.ja_existe) {
        setMsg({ tipo: 'ok', texto: 'Pedido já registado. Aguardamos análise.' })
      } else {
        setMsg({ tipo: 'ok', texto: 'Pedido recebido. Vamos analisar e contactar-te em 24h.' })
        setForm({ nome: '', email: '', telefone: '', nif: '', mensagem: '', hp: '' })
      }
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.message || 'Erro inesperado' })
    } finally {
      setEnviando(false)
    }
  }

  const scrollToForm = () => {
    document.getElementById('pedir-acesso')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const marca = legal.legal_marca || 'LogiStock Pro'

  return (
    <div style={{ minHeight: '100vh', background: T.black, color: T.white, fontFamily: "Inter, 'Segoe UI', system-ui, -apple-system, sans-serif", WebkitFontSmoothing: 'antialiased' }}>

      {/* ─────────── HEADER ─────────── */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.white10}` }}>
        <div style={containerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', gap: '12px' }}>
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src={LOGO_CLARO} alt={marca}
                style={{ height: 'clamp(28px, 5vw, 36px)', width: 'auto', display: 'block', objectFit: 'contain' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline-flex' }} />
              <span style={{ display: 'none', alignItems: 'center', gap: '10px' }}>
                <span style={{ width: '8px', height: '24px', background: T.yellow }} />
                <span style={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: T.white }}>{marca}</span>
              </span>
            </a>
            <button onClick={onShowLogin} style={btnOutlineSm}>
              Entrar
            </button>
          </div>
        </div>
      </header>

      {/* ─────────── HERO ─────────── */}
      <section style={{
        position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${T.white10}`,
        padding: 'clamp(48px, 8vw, 80px) 0 clamp(40px, 7vw, 64px)',
        background: `radial-gradient(ellipse 800px 560px at 80% 20%, rgba(250,204,21,0.10) 0%, rgba(2,2,2,0) 60%), ${T.bgDark}`,
      }}>
        {/* Grid pattern overlay */}
        <div aria-hidden style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }} />
        <div style={{ ...containerStyle, position: 'relative' }}>
          <Eyebrow text="Catálogo B2B · Acesso por convite" />

          <h1 style={{
            fontSize: 'clamp(38px, 10vw, 120px)',
            lineHeight: 0.9, letterSpacing: '-0.05em', fontWeight: 800,
            margin: '20px 0 28px', maxWidth: '1100px',
          }}>
            Stock <mark style={markStyle}>premium</mark> de perfumaria<br />
            e cosmética para profissionais.
          </h1>

          <p style={{
            fontSize: 'clamp(15px, 2vw, 22px)', color: T.white70, lineHeight: 1.55,
            maxWidth: '720px', margin: '0 0 40px',
          }}>
            CHANEL, DIOR, LA MER, TOM FORD e mais 17 marcas top.
            Portes grátis, sem mínimos absurdos, preços só teus.
          </p>

          <div className="hero-ctas" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={scrollToForm} className="cta-primary" style={btnPrimary}>
              Pedir Acesso <ArrowRight size={16} />
            </button>
            <button onClick={onShowLogin} className="cta-outline" style={btnOutline}>
              Já tenho conta
            </button>
          </div>

          {/* Stats inline */}
          <div className="hero-stats" style={{ marginTop: 'clamp(40px, 7vw, 64px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '24px', maxWidth: '720px' }}>
            <Stat numero="21+" label="Marcas premium" />
            <Stat numero="100%" label="Portes incluídos" />
            <Stat numero="24h" label="Resposta ao pedido" />
            <Stat numero="25d" label="Prazo médio entrega" />
          </div>
        </div>
      </section>

      {/* ─────────── MARCAS (yellow band) ─────────── */}
      <section id="marcas" style={{ background: T.yellow, color: T.black, padding: 'clamp(36px, 6vw, 56px) 0', borderBottom: `4px solid ${T.black}` }}>
        <div style={containerStyle}>
          <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: 'clamp(10px, 1.4vw, 13px)', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, margin: '0 0 18px' }}>
            // Marcas no catálogo
          </p>
          <div className="marcas-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', fontSize: 'clamp(13px, 1.8vw, 17px)', fontWeight: 800, letterSpacing: '-0.01em' }}>
            {MARCAS.map((m, i) => (
              <React.Fragment key={m}>
                <span>{m}</span>
                {i < MARCAS.length - 1 && <span className="sep" style={{ opacity: 0.4 }}>·</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── COMO FUNCIONA (dark) ─────────── */}
      <section id="como-funciona" style={{ background: T.black, padding: 'clamp(64px, 10vw, 128px) 0' }}>
        <div style={containerStyle}>
          <Eyebrow text="Como funciona" />
          <h2 style={h2Style}>
            3 passos. <mark style={markStyle}>Zero burocracia.</mark>
          </h2>

          <div style={{ marginTop: '48px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {[
              { n: '01', t: 'Pede acesso', d: 'Preenche o formulário em menos de 1 minuto. NIF, email e tipo de negócio.' },
              { n: '02', t: 'Validamos', d: 'Analisamos o perfil e respondemos por email em 24h úteis. Sem chamadas chatas.' },
              { n: '03', t: 'Encomendas', d: 'Defines password, entras no catálogo e fazes a primeira encomenda no mesmo dia.' },
            ].map(({ n, t, d }) => (
              <div key={n} style={cardDark}>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '12px', letterSpacing: '0.18em', color: T.yellow, fontWeight: 700 }}>{n}</span>
                <h3 style={{ fontSize: 'clamp(20px, 2.5vw, 28px)', fontWeight: 800, letterSpacing: '-0.02em', margin: '14px 0 8px', color: T.white }}>{t}</h3>
                <p style={{ fontSize: '15px', color: T.white70, margin: 0, lineHeight: 1.55 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── BENEFÍCIOS (white) ─────────── */}
      <section style={{ background: T.white, color: T.slate900, padding: 'clamp(64px, 10vw, 128px) 0' }}>
        <div style={containerStyle}>
          <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: 'clamp(10px, 1.4vw, 13px)', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: T.yellow700, margin: '0 0 16px' }}>
            // Vantagens para o teu negócio
          </p>
          <h2 style={{ ...h2Style, color: T.slate900 }}>
            Margens reais. <mark style={markStyle}>Sem ruído.</mark>
          </h2>

          <div style={{ marginTop: '48px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              { icon: Truck, t: 'Portes grátis', d: 'Em todas as encomendas, sem mínimos absurdos. Custo de transporte incluído no preço.' },
              { icon: Lock, t: 'Catálogo privado', d: 'Acesso por convite. Os teus preços só ficam contigo, nunca expostos publicamente.' },
              { icon: Sparkles, t: 'Marcas top', d: 'CHANEL, DIOR, LA MER, TOM FORD e mais. Stock real, origem garantida, factura legal.' },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} style={cardLight}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: T.yellow, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <Icon size={22} color={T.black} strokeWidth={2.2} />
                </div>
                <h3 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 8px', color: T.slate900 }}>{t}</h3>
                <p style={{ fontSize: '15px', color: T.slate600, margin: 0, lineHeight: 1.55 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FAQ (dark, accordion) ─────────── */}
      <section style={{ background: T.black, padding: 'clamp(64px, 10vw, 128px) 0' }}>
        <div style={{ ...containerStyle, maxWidth: '900px' }}>
          <Eyebrow text="Perguntas frequentes" />
          <h2 style={h2Style}>
            Tudo o que precisas <mark style={markStyle}>saber</mark>.
          </h2>

          <div style={{ marginTop: '32px' }}>
            {FAQ.map(({ q, a }) => (
              <details key={q} className="faq-item" style={faqItemStyle}>
                <summary style={faqSummaryStyle}>
                  <span style={{ flex: 1 }}>{q}</span>
                  <Plus size={22} color={T.yellow} className="faq-plus" style={{ flexShrink: 0, transition: 'transform 0.2s' }} />
                </summary>
                <p style={{ margin: '14px 0 0', fontSize: '15px', color: T.white70, lineHeight: 1.7 }}>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FORM PEDIR ACESSO (yellow band) ─────────── */}
      <section id="pedir-acesso" style={{ background: T.yellow, color: T.black, padding: 'clamp(64px, 10vw, 128px) 0' }}>
        <div style={{ ...containerStyle, maxWidth: '720px' }}>
          <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: 'clamp(10px, 1.4vw, 13px)', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, margin: '0 0 16px', display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ display: 'inline-block', width: '32px', height: '2px', background: T.black }} />
            // Pedir acesso ao catálogo
          </p>
          <h2 style={{ fontSize: 'clamp(28px, 6vw, 64px)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, margin: '0 0 16px', color: T.black }}>
            Começa em <span style={{ borderBottom: `6px solid ${T.black}` }}>minutos</span>.
          </h2>
          <p style={{ fontSize: '16px', color: 'rgba(0,0,0,0.7)', margin: '0 0 36px', lineHeight: 1.55 }}>
            Preenche o formulário, validamos o teu perfil em 24h e recebes acesso ao catálogo completo.
          </p>

          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Honeypot */}
            <input type="text" name="hp" value={form.hp} onChange={e => setForm(f => ({ ...f, hp: e.target.value }))}
              tabIndex="-1" autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} />

            <Field label="Nome / Empresa *" value={form.nome} onChange={v => setForm(f => ({ ...f, nome: v }))} required disabled={enviando} />
            <Field label="Email *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} required disabled={enviando} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <Field label="Telefone" type="tel" value={form.telefone} onChange={v => setForm(f => ({ ...f, telefone: v }))} disabled={enviando} />
              <Field label="NIF (opcional)" value={form.nif} onChange={v => setForm(f => ({ ...f, nif: v }))} disabled={enviando} />
            </div>

            <div>
              <label style={labelStyle}>Tipo de negócio (1-2 linhas)</label>
              <textarea
                value={form.mensagem}
                onChange={e => setForm(f => ({ ...f, mensagem: e.target.value }))}
                placeholder="Ex: perfumaria online em Lisboa, barbearia em Braga, revenda…"
                disabled={enviando}
                rows={3}
                style={{ ...inputStyle, height: 'auto', padding: '12px 14px', resize: 'vertical', minHeight: '88px' }}
              />
            </div>

            {msg.texto && (
              <div style={{
                padding: '14px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                background: msg.tipo === 'ok' ? T.black : T.danger,
                color: msg.tipo === 'ok' ? T.yellow : T.white,
                display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                {msg.tipo === 'ok' ? <CheckCircle size={18} /> : <X size={18} />}
                <span>{msg.texto}</span>
              </div>
            )}

            <button type="submit" disabled={enviando}
              style={{
                marginTop: '8px', minHeight: '56px', padding: '0 28px',
                background: T.black, color: T.yellow, border: 'none', borderRadius: '12px',
                fontSize: '14px', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
                cursor: enviando ? 'not-allowed' : 'pointer', opacity: enviando ? 0.7 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                transition: 'transform 0.15s',
              }}
              onMouseEnter={(e) => { if (!enviando) e.currentTarget.style.transform = 'translateY(-1px)' }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}>
              {enviando
                ? (<><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> A enviar…</>)
                : (<>Enviar pedido <ArrowRight size={16} /></>)}
            </button>

            <p style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', textAlign: 'center', margin: '8px 0 0', fontWeight: 500 }}>
              Ao enviar, aceitas os{' '}
              <button type="button" onClick={() => setLegalModal('termos')} style={inlineLinkBtn}>Termos</button>
              {' '}e a{' '}
              <button type="button" onClick={() => setLegalModal('privacidade')} style={inlineLinkBtn}>Política de Privacidade</button>.
            </p>
          </form>
        </div>
      </section>

      {/* ─────────── FOOTER (dark, editorial) ─────────── */}
      <footer style={{ background: T.black, color: T.white, position: 'relative', overflow: 'hidden', borderTop: `1px solid ${T.white10}` }}>
        {/* Subtle radial yellow background */}
        <div aria-hidden style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse 600px 400px at 20% 100%, rgba(250,204,21,0.06) 0%, transparent 70%)',
        }} />

        <div style={{ ...containerStyle, position: 'relative', paddingTop: 'clamp(56px, 9vw, 96px)', paddingBottom: 'clamp(24px, 4vw, 40px)' }}>

          {/* Top: brand + tagline + CTA pedir acesso */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 'clamp(24px, 4vw, 48px)',
            alignItems: 'end', paddingBottom: 'clamp(32px, 5vw, 56px)', borderBottom: `1px solid ${T.white10}`,
          }} className="footer-top">
            <div style={{ minWidth: 0 }}>
              <img src={LOGO_CLARO} alt={marca}
                style={{ height: 'clamp(36px, 6vw, 52px)', width: 'auto', display: 'block', marginBottom: '20px' }}
                onError={(e) => { e.currentTarget.style.display = 'none' }} />
              <p style={{ fontSize: 'clamp(20px, 3vw, 32px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 12px', color: T.white, maxWidth: '560px' }}>
                Catálogo B2B premium para profissionais que não aceitam <mark style={markStyle}>desperdício</mark>.
              </p>
              <p style={{ fontSize: '14px', color: T.white70, margin: 0, lineHeight: 1.6, maxWidth: '480px' }}>
                CHANEL, DIOR, LA MER, TOM FORD e mais 17 marcas. Sem mínimos absurdos. Sem ruído.
              </p>
            </div>
            <button onClick={scrollToForm} style={{ ...btnPrimary, whiteSpace: 'nowrap' }} className="footer-cta">
              Pedir Acesso <ArrowRight size={14} />
            </button>
          </div>

          {/* Middle: 3 colunas Contacto / Conformidade / Navegacao */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'clamp(28px, 4vw, 48px)', paddingTop: 'clamp(32px, 5vw, 48px)', paddingBottom: 'clamp(24px, 4vw, 40px)',
          }}>
            <div>
              <p style={footerLabel}>// Contacto</p>
              <a href="tel:+351221450778" style={footerContactLink}>
                <Phone size={14} style={{ flexShrink: 0 }} />
                <span>+351 22 145 0778</span>
              </a>
              <a href="mailto:comercial@lognow.pt" style={footerContactLink}>
                <Mail size={14} style={{ flexShrink: 0 }} />
                <span>comercial@lognow.pt</span>
              </a>
              <p style={{ ...footerTextMuted, marginTop: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={13} style={{ flexShrink: 0, color: T.yellow }} />
                <span>9h–12h · dias úteis</span>
              </p>
            </div>

            <div>
              <p style={footerLabel}>// Conformidade</p>
              {/* Logo Livro de Reclamacoes: branco por defeito, cor real no hover */}
              <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noopener noreferrer"
                className="livro-reclamacoes-link"
                style={{ display: 'inline-block', marginBottom: '14px' }}
                title="Livro de Reclamações Electrónico">
                <img src="/livro-reclamacoes.png" alt="Livro de Reclamações"
                  style={{ height: '40px', width: 'auto', display: 'block', filter: 'brightness(0) invert(1)', transition: 'filter 0.2s' }} />
              </a>
              <a href="https://www.cniacc.pt" target="_blank" rel="noopener noreferrer" style={footerLinkA}>
                CNIACC · RAL <span style={{ color: T.yellow }}>↗</span>
              </a>
              <button onClick={() => setLegalModal('termos')} style={footerLinkBtn}>Termos e Condições</button>
              <button onClick={() => setLegalModal('privacidade')} style={footerLinkBtn}>Política de Privacidade</button>
            </div>

            <div>
              <p style={footerLabel}>// Navegação</p>
              <a href="#pedir-acesso" style={footerLinkA}>Pedir acesso</a>
              <button onClick={onShowLogin} style={footerLinkBtn}>Já tenho conta</button>
              <a href="#" onClick={(e) => { e.preventDefault(); document.getElementById('marcas')?.scrollIntoView({ behavior: 'smooth' }) }} style={footerLinkA}>Marcas</a>
              <a href="#" onClick={(e) => { e.preventDefault(); document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' }) }} style={footerLinkA}>Como funciona</a>
            </div>
          </div>

          {/* Disclaimer profissional B2B */}
          <div style={{
            padding: 'clamp(16px, 2.5vw, 22px) 0',
            borderTop: `1px solid ${T.white10}`,
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }}>
            <span style={{
              flexShrink: 0, fontFamily: 'ui-monospace, monospace',
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: T.yellow,
              padding: '2px 8px', border: `1px solid ${T.yellow}`, borderRadius: '4px',
              marginTop: '2px',
            }}>
              B2B
            </span>
            <p style={{
              margin: 0, fontSize: 'clamp(12px, 1.6vw, 13px)', lineHeight: 1.65,
              color: T.white70, fontStyle: 'italic',
            }}>
              Todos os preços apresentados são <strong style={{ color: T.white, fontStyle: 'normal' }}>sem IVA incluído</strong>.
              Plataforma destinada exclusivamente a <strong style={{ color: T.white, fontStyle: 'normal' }}>profissionais B2B</strong>{' '}
              para venda exclusiva nas plataformas do grupo <strong style={{ color: T.white, fontStyle: 'normal' }}>Plataforma Concreta Lda</strong>.
            </p>
          </div>

          {/* Parceria LogicPro */}
          <div style={{
            padding: 'clamp(14px, 2vw, 18px) 0',
            borderTop: `1px solid ${T.white10}`,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '10px',
            fontSize: '12px', color: T.white70,
          }}>
            <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: T.white40 }}>
              // parceria
            </span>
            <span>
              <strong style={{ color: T.white }}>LogicPro</strong> é uma parceria entre{' '}
              <strong style={{ color: T.yellow }}>Cátia Andreia Silva</strong>{' '}
              <span style={{ color: T.white40 }}>&</span>{' '}
              <strong style={{ color: T.yellow }}>LogNow</strong>
            </span>
          </div>

          {/* Bottom: copyright + meta */}
          <div style={{
            paddingTop: 'clamp(20px, 3vw, 28px)', borderTop: `1px solid ${T.white10}`,
            display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '12px', color: T.white40,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-block', width: '6px', height: '6px', background: T.yellow, borderRadius: '50%' }} />
              <span>© 2026 Cátia Andreia Silva & LogNow</span>
              <span style={{ color: T.white10 }}>·</span>
              <span>Todos os direitos reservados</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <PwaInstallBanner />
              <span>Made in Portugal 🇵🇹</span>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        details.faq-item summary::-webkit-details-marker { display: none; }
        details.faq-item[open] .faq-plus { transform: rotate(45deg); }
        details.faq-item[open] { background: rgba(255,255,255,0.03); padding-left: 16px; padding-right: 16px; border-radius: 8px; }
        mark { background: ${T.yellow} !important; color: ${T.black} !important; padding: 0 6px; }
        input::placeholder, textarea::placeholder { color: rgba(0,0,0,0.4); }
        input:focus, textarea:focus { outline: 3px solid ${T.black}; outline-offset: 1px; }

        /* Footer link hover */
        footer a:hover { color: ${T.yellow} !important; }
        /* Logo Livro de Reclamacoes: branco por defeito, cor real no hover */
        .livro-reclamacoes-link img { filter: brightness(0) invert(1); transition: filter 0.25s ease, transform 0.2s ease; }
        .livro-reclamacoes-link:hover img { filter: none; transform: translateY(-1px); }

        /* Mobile-first responsiveness (90% trafego e mobile) */
        @media (max-width: 640px) {
          /* Hero CTAs full width em mobile */
          .hero-ctas button { width: 100%; }
          /* Stats hero: 2 colunas em vez de 4 */
          .hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
          /* Footer top: stack vertical */
          .footer-top { grid-template-columns: 1fr !important; align-items: stretch !important; }
          .footer-cta { width: 100% !important; justify-content: center !important; }
          /* CTAs maiores em mobile (touch 44px+) */
          .cta-primary, .cta-outline { min-height: 52px; padding: 0 22px; font-size: 12px; }
        }
        @media (max-width: 480px) {
          /* Marcas em 2 colunas em ecras muito pequenos */
          .marcas-list { gap: 8px 16px !important; }
          .marcas-list span:not(.sep) { font-size: 14px !important; }
        }

        /* Smooth scroll */
        html { scroll-behavior: smooth; }

        /* Prevenir overflow horizontal */
        body, #root { overflow-x: hidden !important; }

        /* Legal modal scrollbar custom */
        .legal-modal-body::-webkit-scrollbar { width: 8px; }
        .legal-modal-body::-webkit-scrollbar-track { background: ${T.zinc950}; }
        .legal-modal-body::-webkit-scrollbar-thumb { background: ${T.yellow}; border-radius: 4px; }
        .legal-modal-body h3 { color: ${T.yellow}; font-size: clamp(15px, 2vw, 18px); font-weight: 800; letter-spacing: -0.01em; margin: 28px 0 10px; }
        .legal-modal-body h3:first-child { margin-top: 0; }
        .legal-modal-body p { color: ${T.white70}; font-size: 15px; line-height: 1.7; margin: 0 0 14px; }
        .legal-modal-body ul { color: ${T.white70}; font-size: 15px; line-height: 1.7; padding-left: 20px; margin: 0 0 14px; }
        .legal-modal-body strong { color: ${T.white}; font-weight: 700; }
        .legal-modal-body a { color: ${T.yellow}; text-decoration: underline; }
        .legal-modal-body em { color: ${T.white40}; font-style: normal; font-family: 'ui-monospace, monospace'; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; }
      `}</style>

      {/* ─────────── MODAL LEGAL (Termos / Privacidade) ─────────── */}
      {legalModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setLegalModal(null) }}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'clamp(12px, 3vw, 24px)',
            animation: 'fadeIn 0.2s ease-out',
          }}>
          <div style={{
            background: T.bgDark, border: `1px solid ${T.white10}`, borderRadius: '16px',
            width: '100%', maxWidth: '820px', maxHeight: 'calc(100vh - 32px)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          }}>
            {/* Header modal */}
            <div style={{
              padding: 'clamp(20px, 3vw, 28px) clamp(20px, 4vw, 36px)',
              borderBottom: `1px solid ${T.white10}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px',
              background: `linear-gradient(180deg, ${T.zinc900} 0%, ${T.bgDark} 100%)`,
            }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ width: '24px', height: '2px', background: T.yellow }} />
                  <span style={{ fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: T.yellow }}>
                    Documento legal
                  </span>
                </div>
                <h2 style={{
                  fontSize: 'clamp(22px, 4vw, 36px)', fontWeight: 800, letterSpacing: '-0.03em',
                  lineHeight: 1.05, margin: 0, color: T.white,
                }}>
                  {legalModal === 'termos' ? 'Termos e Condições' : 'Política de Privacidade'}
                </h2>
              </div>
              <button onClick={() => setLegalModal(null)}
                aria-label="Fechar"
                style={{
                  background: 'transparent', border: `1px solid ${T.white10}`, borderRadius: '10px',
                  color: T.white, cursor: 'pointer', padding: '10px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.yellow; e.currentTarget.style.color = T.black }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.white }}>
                <X size={18} />
              </button>
            </div>

            {/* Body scrollable */}
            <div className="legal-modal-body" style={{
              padding: 'clamp(20px, 3vw, 36px)', overflowY: 'auto', flex: 1,
            }}>
              {legalModal === 'termos' ? <TermosCondicoesContent /> : <PoliticaPrivacidadeContent />}
            </div>

            {/* Footer modal */}
            <div style={{
              padding: 'clamp(14px, 2.5vw, 20px) clamp(20px, 4vw, 36px)',
              borderTop: `1px solid ${T.white10}`, background: T.zinc950,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
            }}>
              <span style={{ fontSize: '11px', color: T.white40, fontFamily: 'ui-monospace, monospace', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Última actualização · Maio 2026
              </span>
              <button onClick={() => setLegalModal(null)}
                style={{
                  background: T.yellow, color: T.black, border: 'none', borderRadius: '8px',
                  padding: '10px 20px', fontSize: '12px', fontWeight: 800, letterSpacing: '0.14em',
                  textTransform: 'uppercase', cursor: 'pointer',
                }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────── Estilos partilhados ───────────
const containerStyle = {
  maxWidth: '1320px',
  margin: '0 auto',
  padding: '0 clamp(20px, 4vw, 56px)',
}

const markStyle = {
  background: T.yellow,
  color: T.black,
  padding: '0 8px',
}

const h2Style = {
  fontSize: 'clamp(28px, 6.5vw, 80px)',
  fontWeight: 800,
  letterSpacing: '-0.04em',
  lineHeight: 0.98,
  margin: '20px 0 0',
  color: T.white,
}

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
  padding: '0 28px', minHeight: '56px',
  background: T.yellow, color: T.black, border: 'none', borderRadius: '12px',
  fontSize: 'clamp(12px, 1.4vw, 14px)', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
  cursor: 'pointer', transition: 'all 0.15s',
}

const btnOutline = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
  padding: '0 28px', minHeight: '56px',
  background: 'transparent', color: T.white, border: `2px solid ${T.white}`, borderRadius: '12px',
  fontSize: 'clamp(12px, 1.4vw, 14px)', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
  cursor: 'pointer', transition: 'all 0.15s',
}

const btnOutlineSm = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  padding: '0 18px', minHeight: '40px',
  background: 'transparent', color: T.white, border: `1px solid ${T.white}`, borderRadius: '8px',
  fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
  cursor: 'pointer', transition: 'all 0.15s',
}

const cardDark = {
  background: T.zinc950, border: `1px solid ${T.white10}`, borderRadius: '20px',
  padding: 'clamp(20px, 3vw, 32px)',
  transition: 'border-color 0.2s',
}

const cardLight = {
  background: T.slate100, borderRadius: '20px',
  padding: 'clamp(24px, 3vw, 32px)',
  border: `1px solid #E2E8F0`,
}

const inputStyle = {
  width: '100%', minHeight: '52px', padding: '0 16px',
  border: `2px solid ${T.black}`, borderRadius: '10px',
  fontSize: '15px', outline: 'none', boxSizing: 'border-box',
  background: T.white, color: T.black, fontWeight: 500,
  fontFamily: 'inherit',
}

const labelStyle = {
  display: 'block', fontSize: '11px', fontWeight: 800, color: T.black,
  marginBottom: '8px', letterSpacing: '0.14em', textTransform: 'uppercase',
}

const inlineLink = { color: T.black, fontWeight: 700, textDecoration: 'underline' }
const inlineLinkBtn = {
  color: T.black, fontWeight: 700, textDecoration: 'underline',
  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit',
}

const faqItemStyle = {
  borderBottom: `1px solid ${T.white10}`,
  padding: '20px 0',
  cursor: 'pointer',
}

const faqSummaryStyle = {
  cursor: 'pointer', listStyle: 'none',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px',
  fontSize: 'clamp(16px, 2vw, 22px)', fontWeight: 700, letterSpacing: '-0.01em', color: T.white,
}

const footerLabel = {
  fontFamily: 'ui-monospace, "JetBrains Mono", monospace', fontSize: '11px',
  letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
  color: T.yellow, margin: '0 0 18px',
}

const footerText = { fontSize: '14px', color: T.white, margin: '0 0 6px', fontWeight: 600 }
const footerTextMuted = { fontSize: '13px', color: T.white40, margin: '0 0 4px', lineHeight: 1.5 }
const footerLink = { fontSize: '13px', color: T.white70, margin: '0 0 6px' }

const footerLinkA = {
  display: 'block', fontSize: '13px', color: T.white70,
  margin: '0 0 10px', textDecoration: 'none',
  transition: 'color 0.15s', lineHeight: 1.4,
}

const footerLinkBtn = {
  display: 'block', fontSize: '13px', color: T.white70,
  margin: '0 0 10px', background: 'transparent', border: 'none', padding: 0,
  textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
  transition: 'color 0.15s', lineHeight: 1.4,
}

const footerContactLink = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  fontSize: '14px', color: T.white, fontWeight: 600,
  textDecoration: 'none', marginBottom: '10px',
  transition: 'color 0.15s', wordBreak: 'break-word',
}

// ─────────── Componentes auxiliares ───────────

function Eyebrow({ text }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ width: 'clamp(32px, 4vw, 44px)', height: '2px', background: T.yellow }} />
      <span style={{ fontSize: 'clamp(11px, 1.3vw, 13px)', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: T.white70 }}>
        {text}
      </span>
    </div>
  )
}

function Stat({ numero, label }) {
  return (
    <div>
      <div style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.04em', color: T.yellow, lineHeight: 1 }}>{numero}</div>
      <div style={{ fontSize: '12px', color: T.white70, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '6px', fontWeight: 600 }}>{label}</div>
    </div>
  )
}

function Field({ label, value, onChange, type='text', required=false, disabled=false }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        style={inputStyle}
      />
    </div>
  )
}

// ─────────── Conteudo: Termos & Condicoes ───────────
function TermosCondicoesContent() {
  return (
    <>
      <p><em>Última actualização · Maio 2026</em></p>

      <h3>0. Identificação do Operador</h3>
      <p>A plataforma <strong>LogiStock Pro</strong> é operada por:</p>
      <ul>
        <li><strong>Cátia Andreia da Silva</strong> — Empresário em Nome Individual (ENI)</li>
        <li><strong>NIF:</strong> 241 288 398</li>
        <li><strong>Sede:</strong> Avenida da Ponte, n.º 75, 4795-550 Negrelos, Portugal</li>
        <li><strong>Telefone:</strong> +351 22 145 0778</li>
        <li><strong>Email:</strong> comercial@lognow.pt</li>
      </ul>

      <h3>1. Aceitação dos Termos</h3>
      <p>Ao aceder e utilizar a plataforma LogiStock Pro (doravante "Plataforma"), o utilizador declara ter lido, compreendido e aceitado integralmente os presentes Termos e Condições. Se não concordar, não deve utilizar a Plataforma.</p>

      <h3>2. Descrição do Serviço</h3>
      <p>O LogiStock Pro é uma plataforma B2B de gestão de stock e encomendas, destinada a clientes empresariais previamente aprovados. Permite consultar catálogo, efectuar encomendas, acompanhar estado de pagamento e gestão de stock, e receber notificações automáticas.</p>

      <h3>3. Conta e Acesso</h3>
      <p>O acesso requer registo prévio e aprovação manual pelo Operador. O utilizador é responsável pela confidencialidade da sua palavra-passe e por todas as actividades realizadas na sua conta. Deve notificar imediatamente o Operador de qualquer uso não autorizado.</p>

      <h3>4. Encomendas e Pagamento</h3>
      <ul>
        <li>As encomendas estão sujeitas a confirmação por parte do Operador.</li>
        <li>O pagamento é realizado em duas prestações: <strong>50% no momento da encomenda</strong> e os restantes <strong>50% antes do envio</strong>.</li>
        <li>Os <strong>portes de envio estão incluídos no preço</strong> de cada produto — sem custos adicionais na expedição.</li>
        <li>Os preços apresentados são em Euros, com indicação separada de IVA quando aplicável.</li>
        <li>O incumprimento do pagamento da segunda prestação no prazo indicado pode levar ao cancelamento da encomenda.</li>
      </ul>

      <h3>5. Cancelamentos e Devoluções</h3>
      <p>O cancelamento de encomendas pode ser solicitado antes da expedição. Devoluções de produto após recepção seguem a legislação portuguesa em vigor, sendo necessário contactar o administrador da Plataforma para iniciar o processo.</p>

      <h3>6. Limitação de Responsabilidade</h3>
      <p>A Plataforma é fornecida no estado em que se encontra. Embora envidemos esforços para manter o serviço disponível e os dados actualizados, não garantimos ausência total de erros, indisponibilidade temporária ou correcção absoluta de informação (preços, stocks). A responsabilidade limita-se ao valor da encomenda em causa.</p>

      <h3>7. Propriedade Intelectual</h3>
      <p>Todo o conteúdo da Plataforma (interface, marca LogiStock Pro, código, textos, gráficos) é propriedade do operador e está protegido pela legislação aplicável. É proibida qualquer reprodução, modificação ou utilização sem autorização escrita.</p>

      <h3>8. Alterações aos Termos</h3>
      <p>O operador reserva-se o direito de alterar estes Termos a qualquer momento. As alterações entram em vigor após publicação na Plataforma. O uso continuado da Plataforma após alterações constitui aceitação dos novos Termos.</p>

      <h3>9. Lei Aplicável</h3>
      <p>Os presentes Termos regem-se pela lei portuguesa. Qualquer litígio será resolvido nos tribunais competentes de Portugal.</p>

      <h3>10. Reclamações</h3>
      <p>Em caso de reclamação, pode utilizar o <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noopener noreferrer">Livro de Reclamações Electrónico</a> ou recorrer à resolução alternativa de litígios através do <a href="https://www.cniacc.pt" target="_blank" rel="noopener noreferrer">CNIACC</a>.</p>

      <h3>11. Contacto</h3>
      <p>Para questões relacionadas com estes Termos, contacte o Operador através do email <strong>comercial@lognow.pt</strong> ou telefone <strong>+351 22 145 0778</strong>.</p>
    </>
  )
}

// ─────────── Conteudo: Política de Privacidade (RGPD) ───────────
function PoliticaPrivacidadeContent() {
  return (
    <>
      <p><em>Última actualização · Maio 2026</em></p>
      <p>A presente Política de Privacidade descreve como o LogiStock Pro recolhe, utiliza, armazena e protege os seus dados pessoais, em conformidade com o Regulamento Geral de Protecção de Dados (RGPD — Regulamento UE 2016/679).</p>

      <h3>1. Responsável pelo Tratamento</h3>
      <p>O responsável pelo tratamento dos seus dados pessoais é:</p>
      <ul>
        <li><strong>Cátia Andreia da Silva</strong> — Empresário em Nome Individual (ENI)</li>
        <li><strong>NIF:</strong> 241 288 398</li>
        <li><strong>Sede:</strong> Avenida da Ponte, n.º 75, 4795-550 Negrelos, Portugal</li>
        <li><strong>Contacto:</strong> comercial@lognow.pt · +351 22 145 0778</li>
      </ul>

      <h3>2. Dados Recolhidos</h3>
      <ul>
        <li><strong>Identificação</strong>: nome, email, telefone, NIF, morada e código postal</li>
        <li><strong>Conta</strong>: palavra-passe (encriptada), data de criação, estado da conta</li>
        <li><strong>Encomendas</strong>: histórico, valores, estado de pagamento, dados de entrega</li>
        <li><strong>Técnicos</strong>: tokens de sessão, registos de acesso (logs), IPs</li>
      </ul>

      <h3>3. Finalidades</h3>
      <ul>
        <li>Gestão da conta e autenticação</li>
        <li>Processamento de encomendas e pagamentos</li>
        <li>Envio de notificações sobre encomendas, alertas de stock e actualizações</li>
        <li>Cumprimento de obrigações legais e contabilísticas</li>
        <li>Melhoria contínua do serviço</li>
      </ul>

      <h3>4. Fundamentos Legais</h3>
      <p>Os dados são tratados com base na <strong>execução do contrato</strong> (encomendas e gestão de conta), em <strong>obrigações legais</strong> (facturação, contabilidade) e no <strong>interesse legítimo</strong> em melhorar o serviço.</p>

      <h3>5. Partilha com Terceiros</h3>
      <p>Os dados são partilhados apenas com prestadores essenciais ao serviço:</p>
      <ul>
        <li><strong>Supabase</strong> (alojamento da base de dados e autenticação)</li>
        <li><strong>Servidor SMTP</strong> (envio de emails transaccionais)</li>
        <li><strong>Transportadoras</strong> (CTT, DHL, etc., apenas dados de entrega)</li>
      </ul>
      <p>Não vendemos nem cedemos os seus dados para fins comerciais a terceiros.</p>

      <h3>6. Conservação dos Dados</h3>
      <p>Os dados pessoais são conservados pelo período necessário ao cumprimento das finalidades indicadas e das obrigações legais (mínimo de 10 anos para facturação, ao abrigo do CIVA).</p>

      <h3>7. Direitos do Titular</h3>
      <p>O utilizador pode, a qualquer momento, exercer os seguintes direitos:</p>
      <ul>
        <li><strong>Acesso</strong> aos seus dados pessoais</li>
        <li><strong>Rectificação</strong> de dados incorrectos ou desactualizados</li>
        <li><strong>Apagamento</strong> ("direito ao esquecimento"), nos limites legais</li>
        <li><strong>Limitação</strong> e <strong>oposição</strong> ao tratamento</li>
        <li><strong>Portabilidade</strong> dos seus dados</li>
      </ul>
      <p>Para exercer estes direitos, contacte-nos pelo email <strong>comercial@lognow.pt</strong>. Pode também apresentar reclamação à Comissão Nacional de Protecção de Dados (CNPD).</p>

      <h3>8. Segurança</h3>
      <p>Aplicamos medidas técnicas e organizacionais adequadas: encriptação de palavras-passe, RLS (Row-Level Security) em todas as tabelas, comunicações HTTPS, acesso restrito a administradores autorizados.</p>

      <h3>9. Cookies e Tecnologias Semelhantes</h3>
      <p>A Plataforma utiliza cookies essenciais para autenticação e funcionamento (session tokens). Não utiliza cookies de marketing ou rastreamento publicitário de terceiros.</p>

      <h3>10. Alterações à Política</h3>
      <p>Esta Política pode ser actualizada periodicamente. As alterações relevantes serão comunicadas aos utilizadores activos.</p>
    </>
  )
}
